import { closestCenter, DndContext, DragEndEvent, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import { arrayMove, rectSortingStrategy, SortableContext, sortableKeyboardCoordinates, useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import React, { useCallback, useEffect, useState } from 'react'
import { Button, Modal, Spinner } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { Document, Page, pdfjs } from 'react-pdf'
import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'
import { apiService } from '../../services/api.service'
import { documentService } from '../../services/document.service'
import { toastService } from '../../services/toast.service'
import { DocumentationDialog } from './DocumentationDialog'
import './PDFEditor.scss'

if (typeof window !== 'undefined') {
  pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js'
}

export enum PdfEditorEditMode {
  Update = 'update',
  Create = 'create',
}

interface PageOperation {
  page: number
  rotate: number
  baseRotate?: number
  splitAfter: boolean
  selected?: boolean
  loaded?: boolean
}

interface PDFEditorProps {
  show: boolean
  onHide: () => void
  documentId: number
  onSuccess?: (result: { updatedInPlace: boolean; deletedOriginal: boolean }) => void
}

interface SortablePageItemProps {
  pageOp: PageOperation
  index: number
  blobUrl: string
  isSelected: boolean
  onToggleSelection: (index: number) => void
  onRotate: (index: number, counterclockwise?: boolean) => void
  onRemove: (index: number) => void
  onToggleSplit: (index: number) => void
  onPageLoad: (index: number, baseRotate: number) => void
  onPageRendered: (index: number) => void
}

const SortablePageItem: React.FC<SortablePageItemProps> = ({
  pageOp,
  index,
  blobUrl,
  isSelected,
  onToggleSelection,
  onRotate,
  onRemove,
  onToggleSplit,
  onPageLoad,
  onPageRendered,
}) => {
  const { t } = useTranslation()
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: `page-${index}` })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  const displayRotate = ((pageOp.baseRotate ?? 0) + pageOp.rotate + 360) % 360
  const rotateProp =
    pageOp.baseRotate === undefined && pageOp.rotate === 0 ? undefined : displayRotate

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`pdf-page-item rounded p-2 ${isSelected ? 'selected' : ''}`}
      onClick={() => onToggleSelection(index)}
    >
      <div className="pdf-page-actions hover-actions z-10">
        <div className="btn-group-vertical btn-group-sm">
          <button
            className="btn btn-dark"
            disabled={pageOp.baseRotate === undefined}
            onClick={(e) => {
              e.stopPropagation()
              onRotate(index, true)
            }}
            title={t('rotate_page_counter_clockwise') || 'Rotiraj stranicu levo'}
          >
            <i className="bi bi-arrow-counterclockwise"></i>
          </button>
          <button
            className="btn btn-dark"
            disabled={pageOp.baseRotate === undefined}
            onClick={(e) => {
              e.stopPropagation()
              onRotate(index, false)
            }}
            title={t('rotate_page_clockwise') || 'Rotiraj stranicu desno'}
          >
            <i className="bi bi-arrow-clockwise"></i>
          </button>
          <button
            className="btn btn-dark text-danger"
            onClick={(e) => {
              e.stopPropagation()
              onRemove(index)
            }}
            title={t('delete_page') || 'Obriši stranicu'}
          >
            <i className="bi bi-trash"></i>
          </button>
          <button
            className={`btn btn-dark ${pageOp.splitAfter ? 'active' : ''}`}
            onClick={(e) => {
              e.stopPropagation()
              onToggleSplit(index)
            }}
            title={t('add__remove_document_split_here') || 'Podeli dokument ovde'}
          >
            <i className="bi bi-scissors"></i>
          </button>
        </div>
      </div>
      <div className="border-end border-bottom bg-light py-1 px-2 document-check z-10">
        <div className="form-check">
          <input
            type="checkbox"
            className="form-check-input"
            id={`page${index}`}
            checked={isSelected}
            onChange={() => onToggleSelection(index)}
            onClick={(e) => e.stopPropagation()}
          />
          <label className="form-check-label" htmlFor={`page${index}`}></label>
        </div>
      </div>
      <div className="pdf-page-number">
        <span className="badge bg-dark">{index + 1}</span>
      </div>
      <div className={`pdf-viewer-container w-100 ${isSelected ? 'selected' : ''}`}>
        {!pageOp.loaded && (
          <div className="placeholder-glow w-100 h-100 z-10">
            <span className="placeholder w-100 h-100"></span>
          </div>
        )}
        <Document file={blobUrl} loading="">
          <Page
            pageNumber={pageOp.page}
            rotate={rotateProp}
            height={320}
            renderTextLayer={false}
            renderAnnotationLayer={false}
            onLoadSuccess={(page) => {
              if (pageOp.baseRotate === undefined) {
                onPageLoad(index, page.rotate ?? 0)
              }
            }}
            onRenderSuccess={() => {
              if (!pageOp.loaded) {
                onPageRendered(index)
              }
            }}
            className={pageOp.loaded ? 'fade show' : 'fade'}
          />
        </Document>
      </div>
      {pageOp.splitAfter && (
        <div className="split-after rounded position-absolute top-0 end-0 bg-dark text-uppercase text-center h-100 px-1 small fw-bold">
          &mdash; <span>{t('split_here') || 'Split here'}</span> &mdash;
        </div>
      )}
      <div
        {...attributes}
        {...listeners}
        className="position-absolute top-0 start-0 w-100 h-100"
        style={{ cursor: isDragging ? 'grabbing' : 'grab', zIndex: 1, pointerEvents: isDragging ? 'auto' : 'none' }}
      />
    </div>
  )
}

const PDFEditor: React.FC<PDFEditorProps> = ({ show, onHide, documentId, onSuccess }) => {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)
  const [pages, setPages] = useState<PageOperation[]>([])
  const [totalPages, setTotalPages] = useState(0)
  const [editMode, setEditMode] = useState<PdfEditorEditMode>(PdfEditorEditMode.Create)
  const [deleteOriginal, setDeleteOriginal] = useState(false)
  const [includeMetadata, setIncludeMetadata] = useState(true)
  const [blobUrl, setBlobUrl] = useState<string | null>(null)
  const [buttonsEnabled, setButtonsEnabled] = useState(true)
  const [showDocsDialog, setShowDocsDialog] = useState(false)

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  useEffect(() => {
    if (!show) return

    let mounted = true
    let createdUrl: string | null = null

    setBlobUrl(null)
    setPages([])
    setTotalPages(0)
    setButtonsEnabled(true)
    setLoading(false)

    apiService
      .get(`documents/${documentId}/preview/?original=true`, { responseType: 'blob' })
      .then((res) => {
        if (!mounted) return
        const blob = res.data as Blob
        const url = URL.createObjectURL(blob)
        createdUrl = url
        setBlobUrl(url)
      })
      .catch((error) => {
        if (!mounted) return
        toastService.showError(t('error_loading_pdf') || 'Error loading PDF', error)
      })

    return () => {
      mounted = false
      if (createdUrl) {
        URL.revokeObjectURL(createdUrl)
      }
    }
  }, [show, documentId, t])

  const handleDocumentLoadSuccess = useCallback(({ numPages }: { numPages: number }) => {
    setTotalPages(numPages)
    setPages(
      Array.from({ length: numPages }, (_, i) => ({
        page: i + 1,
        rotate: 0,
        splitAfter: false,
        selected: false,
        loaded: false,
      }))
    )
  }, [])

  const handlePageLoad = useCallback((index: number, baseRotate: number) => {
    setPages((prev) => prev.map((p, idx) => (idx === index && p.baseRotate === undefined ? { ...p, baseRotate } : p)))
  }, [])

  const toggleSelection = (i: number) => {
    setPages((prev) => prev.map((p, idx) => (idx === i ? { ...p, selected: !p.selected } : p)))
  }

  const rotate = (i: number, counterclockwise: boolean = false) => {
    setPages((prev) =>
      prev.map((p, idx) =>
        idx === i
          ? {
              ...p,
              rotate: (p.rotate + (counterclockwise ? -90 : 90) + 360) % 360,
            }
          : p
      )
    )
  }

  const rotateSelected = (dir: number) => {
    setPages((prev) => prev.map((p) => (p.selected ? { ...p, rotate: (p.rotate + dir + 360) % 360 } : p)))
  }

  const remove = (i: number) => {
    setPages((prev) => prev.filter((_, idx) => idx !== i))
  }

  const toggleSplit = (i: number) => {
    setPages((prev) => {
      const newPages = prev.map((p, idx) => (idx === i ? { ...p, splitAfter: !p.splitAfter } : p))
      if (newPages.some((p) => p.splitAfter)) {
        setEditMode(PdfEditorEditMode.Create)
      }
      return newPages
    })
  }

  const selectAll = () => {
    setPages((prev) => prev.map((p) => ({ ...p, selected: true })))
  }

  const deselectAll = () => {
    setPages((prev) => prev.map((p) => ({ ...p, selected: false })))
  }

  const deleteSelected = () => {
    setPages((prev) => prev.filter((p) => !p.selected))
  }

  const hasSelection = (): boolean => {
    return pages.some((p) => p.selected)
  }

  const canRotateSelected = (): boolean => {
    const selected = pages.filter((p) => p.selected)
    return selected.length > 0 && selected.every((p) => p.baseRotate !== undefined)
  }

  const hasSplit = (): boolean => {
    return pages.some((p) => p.splitAfter)
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (over && active.id !== over.id) {
      setPages((items) => {
        const oldIndex = items.findIndex((_, idx) => `page-${idx}` === active.id)
        const newIndex = items.findIndex((_, idx) => `page-${idx}` === over.id)
        return arrayMove(items, oldIndex, newIndex)
      })
    }
  }

  const computeDocIndex = (index: number): number => {
    let docIndex = 0
    for (let i = 0; i <= index; i++) {
      if (pages[i]?.splitAfter && i < index) docIndex++
    }
    return docIndex
  }

  const getOperations = () => {
    return pages.map((p, idx) => ({
      page: p.page,
      rotate: p.rotate,
      doc: computeDocIndex(idx),
    }))
  }

  const handleConfirm = async () => {
    if (pages.length === 0) return

    setLoading(true)
    setButtonsEnabled(false)
    const updatedInPlace = editMode === PdfEditorEditMode.Update
    const deletedOriginal = !updatedInPlace && deleteOriginal
    try {
      await documentService.bulkEditOperation([documentId], 'edit_pdf', {
        operations: getOperations(),
        delete_original: deletedOriginal,
        update_document: updatedInPlace,
        include_metadata: includeMetadata,
      })
      setLoading(false)
      onHide()
      toastService.showInfo(t('pdf_edit_operation_will_begin') || 'PDF edit operation will begin in the background.')
      if (onSuccess) onSuccess({ updatedInPlace, deletedOriginal })
    } catch (error) {
      setLoading(false)
      setButtonsEnabled(true)
      toastService.showError(t('error_executing_pdf_edit_operation') || 'Error executing PDF edit operation', error)
    }
  }

  return (
    <Modal show={show} onHide={onHide} size="xl" backdrop="static" scrollable dialogClassName="pdf-editor-modal">
      {blobUrl && totalPages === 0 && (
        <div style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', overflow: 'hidden' }}>
          <Document file={blobUrl} onLoadSuccess={handleDocumentLoadSuccess}>
            <Page pageNumber={1} scale={0.1} renderTextLayer={false} renderAnnotationLayer={false} />
          </Document>
        </div>
      )}

      <Modal.Header closeButton>
        <Modal.Title>
          {t('pdf_editor') || 'PDF Editor'}
          <button
            type="button"
            className="btn btn-link p-0 ms-2"
            onClick={() => setShowDocsDialog(true)}
            title={t('documentation') || 'Dokumentacija'}
          >
            <i className="bi bi-info-circle"></i>
          </button>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="pdf-editor-body">
        <div className="pdf-editor-info mb-3 p-2 bg-light rounded">
          <div className="d-flex align-items-center gap-3 flex-nowrap">
            <div className="d-flex align-items-center gap-2 flex-shrink-0">
              <i className="bi bi-file-earmark-pdf text-primary"></i>
              <span className="fw-semibold">{t('total_pages') || 'Ukupno stranica'}:</span>
              <span className="badge bg-primary">{totalPages}</span>
            </div>
            {hasSelection() && (
              <div className="d-flex align-items-center gap-2 flex-shrink-0">
                <i className="bi bi-check-square text-success"></i>
                <span className="fw-semibold">{t('selected_pages') || 'Izabrano stranica'}:</span>
                <span className="badge bg-success">{pages.filter((p) => p.selected).length}</span>
              </div>
            )}
            {pages.length !== totalPages && (
              <div className="d-flex align-items-center gap-2 flex-shrink-0">
                <i className="bi bi-trash text-danger"></i>
                <span className="fw-semibold">{t('deleted_pages') || 'Obrisano stranica'}:</span>
                <span className="badge bg-danger">{totalPages - pages.length}</span>
              </div>
            )}
            <div className="vr flex-shrink-0" style={{ height: '1.5rem', width: '1px', backgroundColor: 'var(--bs-border-color)' }}></div>
            <div className="d-flex flex-wrap gap-2 align-items-center flex-grow-1">
              <div className="btn-group">
                <button className="btn btn-sm btn-outline-secondary" onClick={selectAll} title={t('select_all_pages') || 'Select all pages'}>
                  <i className="bi bi-check-all me-1"></i>
                  <span className="d-none d-sm-inline">{t('select_all') || 'Izaberi sve'}</span>
                </button>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={deselectAll}
                  disabled={!hasSelection()}
                  title={t('deselect_all_pages') || 'Deselect all pages'}
                >
                  <i className="bi bi-x me-1"></i>
                  <span className="d-none d-sm-inline">{t('deselect_all') || 'Poništi izbor'}</span>
                </button>
              </div>
              <div className="btn-group">
                <button
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => rotateSelected(-90)}
                  disabled={!canRotateSelected()}
                  title={t('rotate_selected_pages_counter_clockwise') || 'Rotate selected pages counter-clockwise'}
                >
                  <i className="bi bi-arrow-counterclockwise me-1"></i>
                  <span className="d-none d-sm-inline">{t('rotate_left') || 'Rotiraj levo'}</span>
                </button>
                <button
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => rotateSelected(90)}
                  disabled={!canRotateSelected()}
                  title={t('rotate_selected_pages_clockwise') || 'Rotate selected pages clockwise'}
                >
                  <i className="bi bi-arrow-clockwise me-1"></i>
                  <span className="d-none d-sm-inline">{t('rotate_right') || 'Rotiraj desno'}</span>
                </button>
              </div>
              <button
                className="btn btn-sm btn-outline-danger"
                onClick={deleteSelected}
                disabled={!hasSelection()}
                title={t('delete_selected_pages') || 'Delete selected pages'}
              >
                <i className="bi bi-trash me-1"></i>
                <span className="d-none d-sm-inline">{t('delete_selected') || 'Obriši izabrane'}</span>
              </button>
            </div>
          </div>
        </div>

        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={pages.map((_, idx) => `page-${idx}`)} strategy={rectSortingStrategy}>
            <div className="pdf-pages-grid">
              {blobUrl &&
                pages.map((pageOp, i) => (
                  <SortablePageItem
                    key={`page-${pageOp.page}`}
                    pageOp={pageOp}
                    index={i}
                    blobUrl={blobUrl}
                    isSelected={pageOp.selected || false}
                    onToggleSelection={toggleSelection}
                    onRotate={rotate}
                    onRemove={remove}
                    onToggleSplit={toggleSplit}
                    onPageLoad={handlePageLoad}
                    onPageRendered={(idx) => {
                      setPages((prev) => prev.map((p, pi) => (pi === idx ? { ...p, loaded: true } : p)))
                    }}
                  />
                ))}
            </div>
          </SortableContext>
        </DndContext>
      </Modal.Body>
      <Modal.Footer className="pdf-editor-footer">
        <div className="w-100">
          <div className="row g-2 mb-1">
            <div className="col-12 col-md-6">
              <label className="form-label fw-semibold mb-1 small">
                <i className="bi bi-file-earmark-plus me-2"></i>
                {t('action') || 'Akcija'}
              </label>
              <div className="btn-group w-100" role="group">
                <input
                  type="radio"
                  className="btn-check"
                  id="editModeCreate"
                  name="editMode"
                  checked={editMode === PdfEditorEditMode.Create}
                  onChange={() => setEditMode(PdfEditorEditMode.Create)}
                />
                <label className="btn btn-outline-primary" htmlFor="editModeCreate">
                  <i className="bi bi-plus-circle me-2"></i>
                  {t('create_new_document') || 'Napravi novi dokument(e)'}
                </label>
                <input
                  type="radio"
                  className="btn-check"
                  id="editModeUpdate"
                  name="editMode"
                  checked={editMode === PdfEditorEditMode.Update}
                  onChange={() => setEditMode(PdfEditorEditMode.Update)}
                  disabled={hasSplit()}
                />
                <label className={`btn btn-outline-primary ${hasSplit() ? 'disabled' : ''}`} htmlFor="editModeUpdate">
                  <i className="bi bi-pencil me-2"></i>
                  {t('update_existing_document') || 'Ažuriraj postojeći dokument'}
                </label>
              </div>
              {hasSplit() && (
                <small className="text-muted d-block mt-0" style={{ fontSize: '0.75rem' }}>
                  <i className="bi bi-info-circle me-1"></i>
                  {t('split_detected_update_disabled') || 'Podela dokumenta detektovana - ažuriranje onemogućeno'}
                </small>
              )}
            </div>
            {editMode === PdfEditorEditMode.Create && (
              <div className="col-12 col-md-6">
                <label className="form-label fw-semibold mb-1 small">
                  <i className="bi bi-gear me-2"></i>
                  {t('options') || 'Opcije'}
                </label>
                <div className="d-flex flex-row gap-3">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="copyMeta"
                      checked={includeMetadata}
                      onChange={(e) => setIncludeMetadata(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="copyMeta">
                      <i className="bi bi-tags me-1"></i>
                      {t('copy_metadata') || 'Kopiraj metapodatke'}
                    </label>
                  </div>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="deleteOriginal"
                      checked={deleteOriginal}
                      onChange={(e) => setDeleteOriginal(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="deleteOriginal">
                      <i className="bi bi-trash me-1"></i>
                      {t('delete_original') || 'Izbriši original'}
                    </label>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="d-flex justify-content-end gap-2">
            <Button variant="secondary" onClick={onHide} disabled={!buttonsEnabled}>
              {t('cancel') || 'Otkaži'}
            </Button>
            <Button variant="primary" onClick={handleConfirm} disabled={!buttonsEnabled || pages.length === 0}>
              {loading && <Spinner animation="border" size="sm" className="me-2" />}
              <i className="bi bi-check-circle me-2"></i>
              {t('proceed') || 'Nastavi'}
            </Button>
          </div>
        </div>
      </Modal.Footer>

      <DocumentationDialog show={showDocsDialog} onHide={() => setShowDocsDialog(false)} docKey="pdf_editor" />
    </Modal>
  )
}

export default PDFEditor
